import { useReactiveVar } from '@apollo/client';
import { theme } from 'aimo-ui';
import MenuItem from 'components/common/MenuItem';
import SvgImage from 'components/svgImage/SvgImage';
import { RemoteTutorial } from 'components/tutorial/RemoteTutorial';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { isArray } from 'lodash';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { featureCountryCode } from 'states/persistInStorage';
import {
  ColumnWithMargins,
  ScreenContainer,
  SheetDetailsWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { isWeb } from 'utils/deviceUtils';
import { parseFeatureFlagObject } from 'utils/featureFlagUtils';
import { openTutorial } from 'utils/tutorialUtils';

const { height } = Dimensions.get('window');

const styles = StyleSheet.create({
  scrollViewContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  tutorialCarContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  pinkCircle: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: theme.colors.lightBg.pink,
  },
});

const TutorialContent = () => {
  const { t } = useTranslation();

  const featureCountry = useReactiveVar(featureCountryCode);
  const {
    flags: { remote_tutorial },
  } = useContext(FeatureFlagsContext);

  const remoteTutorialData = useMemo(() => {
    const remoteData = parseFeatureFlagObject<RemoteTutorial[]>(
      remote_tutorial,
      'remote_tutorial'
    );
    return isArray(remoteData) ? remoteData : [];
  }, [remote_tutorial]);

  const filteredTutorials = useMemo(() => {
    return remoteTutorialData.filter((tutorial) => {
      if (!tutorial.countries) {
        return tutorial.reviewable === true;
      }
      return (
        featureCountry &&
        tutorial.countries.includes(featureCountry) &&
        tutorial.reviewable === true
      );
    });
  }, [remoteTutorialData, featureCountry]);
  return (
    <ScrollView contentContainerStyle={styles.scrollViewContainer}>
      <StyledWrapper
        mb={20}
        center={isWeb}
        ml={20}
        mr={20}
        maxWidth={isWeb ? 600 : undefined}>
        <ColumnWithMargins mt={8}>
          <MenuItem
            menuLabel={t('account.tutorials.welcome')}
            withArrow
            onPress={() => openTutorial('firstTimer')}
            testID={'contact-card-phone'}
          />
          {filteredTutorials.map((tutorial) => (
            <MenuItem
              key={tutorial.tutorialId}
              withArrow
              menuLabel={t(`account.tutorials.${tutorial.tutorialId}`)}
              onPress={() => openTutorial(tutorial.tutorialId)}
              testID={`tutorial-${tutorial.tutorialId}`}
            />
          ))}
        </ColumnWithMargins>
      </StyledWrapper>
      {filteredTutorials.length <= 2 && !isWeb && (
        <View style={styles.tutorialCarContainer}>
          <View style={styles.pinkCircle} />
          <SvgImage
            height={100}
            width={100}
            name={'Tutorial_Car'}
            testID={'tutorial-car'}
          />
        </View>
      )}
    </ScrollView>
  );
};

const TutorialSheet = () => {
  if (isWeb) {
    return (
      <ScrollView>
        <ColumnWithMargins mt={30}>
          <StyledWrapper
            mb={20}
            center={isWeb}
            maxWidth={isWeb ? 600 : undefined}>
            <TutorialContent />
          </StyledWrapper>
        </ColumnWithMargins>
      </ScrollView>
    );
  }

  return (
    <SheetDetailsWrapper resetPadding>
      <TutorialContent />
    </SheetDetailsWrapper>
  );
};

const Tutorials: React.FC = () => {
  return (
    <ScreenContainer mt={isWeb ? 190 : 0}>
      <TutorialSheet />
    </ScreenContainer>
  );
};

export default Tutorials;
